import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TESTSTUFE, canInAnyScope } from '@testflow/shared';
import type { Teststufe } from '@testflow/shared';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toIsoOrNull } from '../../lib/date-input.js';
import { type TestPlan, plansApi } from '../../lib/plans-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { useCurrentProject } from '../../lib/use-project.js';
import { PlanStatusChip } from './plan-status-actions.js';

// Design-Spec §3.6 (Screen 3b, Testplan): diese Seite ist der Einstieg — eine Kartenliste der
// Testpläne des aktuellen Projekts plus "Neuer Testplan"-CTA. Ein Klick auf eine Karte
// navigiert zur Detailseite (`/plans/:planId`, Task 18). Diese Route existiert in app-shell.tsx
// noch nicht (kommt mit Task 18) — bis dahin greift die Catch-all-Route der App-Shell, was laut
// Task-17-Brief für diesen Task akzeptiert ist.

type NewPlanValues = {
  name: string;
  teststufe: Teststufe;
  window_from: string | null;
  target_date: string | null;
};

function NewPlanForm({
  pending,
  onCreate,
  onCancel,
}: {
  pending: boolean;
  onCreate: (values: NewPlanValues) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [teststufe, setTeststufe] = useState<Teststufe>(TESTSTUFE[0]);
  const [windowFrom, setWindowFrom] = useState('');
  const [targetDate, setTargetDate] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate({
      name: trimmed,
      teststufe,
      window_from: toIsoOrNull(windowFrom),
      target_date: toIsoOrNull(targetDate),
    });
  };

  return (
    <form
      onSubmit={submit}
      className="flex flex-wrap items-end gap-3 rounded-card border border-[var(--border)] bg-bg-surface p-4"
    >
      <label className="flex flex-col gap-1 text-xs text-text-muted">
        {t('plans.name')}
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-56 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-text-muted">
        {t('plans.teststufe.label')}
        <select
          value={teststufe}
          onChange={(e) => setTeststufe(e.target.value as Teststufe)}
          className="w-44 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
        >
          {TESTSTUFE.map((stufe) => (
            <option key={stufe} value={stufe}>
              {t(`plans.teststufe.${stufe}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs text-text-muted">
        {t('plans.windowFrom')}
        <input
          type="date"
          value={windowFrom}
          onChange={(e) => setWindowFrom(e.target.value)}
          className="w-40 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-text-muted">
        {t('plans.targetDate')}
        <input
          type="date"
          value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
          className="w-40 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1.5 text-sm text-text-primary"
        />
      </label>
      {/* #114 (A9): das Aktiv-Häkchen ist hier ersatzlos weg. Ein neuer Plan ist IMMER ein Entwurf;
          aktiviert wird er bewusst auf der Detailseite, wo die Bedingung „höchstens ein aktiver
          Plan je Projekt" geprüft wird. Ein Häkchen im Anlegen-Formular hätte diese Bedingung an
          einer Stelle gestellt, an der der Nutzer die anderen Pläne gar nicht sieht. */}
      <p className="w-full max-w-md text-[11px] leading-snug text-text-muted sm:w-auto">
        {t('plans.newPlanIsDraftHint')}
      </p>
      <button
        type="submit"
        disabled={pending}
        className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
      >
        {t('plans.save')}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-btn px-2 py-1.5 text-sm text-text-muted"
      >
        {t('plans.cancel')}
      </button>
    </form>
  );
}

function PlanCard({ plan, onSelect }: { plan: TestPlan; onSelect: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex flex-col items-start gap-2 rounded-card border border-[var(--border)] bg-bg-surface p-4 text-left hover:border-brand-accent"
    >
      <div className="flex w-full items-center justify-between gap-2">
        <span className="font-bold text-text-primary">{plan.name}</span>
        {/* #114: der Status steht jetzt an JEDER Karte. Vorher trugen nur aktive Pläne ein Chip —
            ein abgeschlossener Plan sah in der Liste aus wie ein frischer Entwurf. */}
        <PlanStatusChip status={plan.status} />
      </div>
      {/* Scope-Modus in einem eigenen <span>: nur so bleibt der Text per getByText/findByText
          isolierbar — als gemeinsamer Textknoten mit dem Teststufe-Text wäre er nur als
          verkettete Zeichenkette auffindbar (RTL matcht Textinhalt pro Element, nicht Teilstrings).
          Das zusätzliche {' '} nach dem Trennzeichen ist Pflicht: JSX verwirft Zeilenumbruch-
          Whitespace direkt vor einem Tag ersatzlos, sonst fehlt der Abstand vor dem <span>. */}
      <span className="text-sm text-text-muted">
        {t(`plans.teststufe.${plan.teststufe}`)} {'·'}{' '}
        <span>{t(`plans.scopeMode.${plan.scope_mode}`)}</span>
      </span>
    </button>
  );
}

export function PlansPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useScopedUser();
  const { project, isLoading: projectLoading } = useCurrentProject();
  const [formOpen, setFormOpen] = useState(false);

  // #58: Testpläne anlegen ist `plan.manage` (Backend erzwingt es zusätzlich). Ohne das Recht
  // wird weder der "Neuer Plan"-CTA noch das Anlege-Formular gezeigt — die Kartenliste bleibt
  // für alle lesbar.
  const canManage = user ? canInAnyScope(user, 'plan.manage') : false;

  const projectId = project?.project_id ?? '';

  const { data: plans, isLoading } = useQuery({
    queryKey: ['plans', projectId],
    queryFn: () => plansApi.list(projectId),
    enabled: Boolean(projectId),
  });

  const createPlan = useMutation({
    mutationFn: (body: NewPlanValues) => plansApi.create(projectId, body),
    meta: { successToast: 'feedback.created' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['plans', projectId] });
      setFormOpen(false);
    },
  });

  if (projectLoading) {
    return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  }

  if (!project) {
    return <div className="p-6 text-text-muted">{t('plans.noProject')}</div>;
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-text-primary">{t('plans.title')}</h1>
        {canManage && !formOpen && (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white"
          >
            {t('plans.new')}
          </button>
        )}
      </div>

      {canManage && formOpen && (
        <NewPlanForm
          pending={createPlan.isPending}
          onCreate={(values) => createPlan.mutate(values)}
          onCancel={() => setFormOpen(false)}
        />
      )}

      {isLoading && <div className="text-sm text-text-muted">{t('common.loading')}</div>}
      {!isLoading && plans?.length === 0 && (
        <div className="text-sm text-text-muted">{t('plans.empty')}</div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {plans?.map((plan) => (
          <PlanCard
            key={plan.test_plan_id}
            plan={plan}
            onSelect={() => navigate(`/plans/${plan.test_plan_id}`)}
          />
        ))}
      </div>
    </div>
  );
}
